import React, { useEffect, useState } from "react";
import { FormikType } from "../../../../types/types";
import { InitialAuditionStateType } from "../../../../assets/data/initialState";
import {
  addCompanyTypeFilter,
  deleteCompanyTypeFilter,
  selectedCompanyTypeFilterSelector,
  selectedCompanyTypeFiltersSelector,
  setSelectedCompanyTypeFilter,
  updateSelectedCompanyTypeFilter,
} from "../../../../redux/slices/companyTypeFiltersSlice";
import { useIADispatch, useIASelector } from "../../../../redux/hooks";
import {
  useCreateCompanyTypeFilterMutation,
  useDeleteCompanyTypeFilterMutation,
  useUpdateCompanyTypeFilterMutation,
} from "../../../../services/CompanyTypeFiltersServices/CompanyTypeFiltersServices";
import { CompanyTypeFilterTitleSchema } from "../../../../utils/validationSchema";
import { useFormik } from "formik";
import asyncTimeout from "../../../../utils/asyncTimeout";
import { toast } from "react-toastify";
import { isErrorWithMessage } from "../../../../services/helper";
import TextInput from "../../inputfields/TextInput";
import CustomButton from "../../buttons/CustomButton";

type Props = FormikType<InitialAuditionStateType>;

export default function CompanyTypeFilterModalEdit({ formik }: Props) {
  const selectedCompanyTypeFilter = useIASelector(
    selectedCompanyTypeFilterSelector
  );
  const selectedCompanyTypeFilters = useIASelector(
    selectedCompanyTypeFiltersSelector
  );
  const dispatch = useIADispatch();

  const [createCompanyTypeFilter] = useCreateCompanyTypeFilterMutation();
  const [updateCompanyTypeFilter] = useUpdateCompanyTypeFilterMutation();
  const [deleteCompanyTypeFilterQuery] = useDeleteCompanyTypeFilterMutation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [isUpdate, setIsUpdate] = useState<boolean>(false);

  useEffect(() => {
    if (!isUpdate) return;
    formik.setFieldValue("companyTypeFilters", selectedCompanyTypeFilters);
    setIsUpdate(false);
  }, [selectedCompanyTypeFilters, isUpdate]);

  useEffect(() => {
    setFieldTouched("title", false);
    setFieldError("title", "");

    if (!selectedCompanyTypeFilter) {
      setFieldValue("title", "");
      return;
    }
    setFieldValue("title", selectedCompanyTypeFilter.title);
  }, [selectedCompanyTypeFilter]);

  const onSave = async () => {
    setIsLoading(true);
    try {
      const [response] = await Promise.all([
        createCompanyTypeFilter({
          title: values.title,
        }).unwrap(),
        asyncTimeout(300),
      ]);

      if (response) {
        toast.success("Successfully created");
        dispatch(addCompanyTypeFilter(response));
      }
    } catch (error) {
      toast.error(
        isErrorWithMessage(error)
          ? error.data.data.message
          : "Something went wrong. Please try again"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onUpdate = async () => {
    if (!selectedCompanyTypeFilter) return;

    setIsLoading(true);

    try {
      const [response] = await Promise.all([
        updateCompanyTypeFilter({
          id: selectedCompanyTypeFilter.id,
          title: values.title,
        }).unwrap(),
        asyncTimeout(300),
      ]);

      if (response) {
        toast.success("Successfully updated");
        dispatch(updateSelectedCompanyTypeFilter(response));
        setIsUpdate(true);
      }
    } catch (error) {
      console.log("error", error);
      toast.error(
        isErrorWithMessage(error)
          ? error.data.data.message
          : "Something went wrong. Please try again"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onDelete = async () => {
    setIsDeleting(true);
    if (!selectedCompanyTypeFilter) return;
    try {
      const [response] = await Promise.all([
        deleteCompanyTypeFilterQuery({
          id: selectedCompanyTypeFilter.id,
        }).unwrap(),
        asyncTimeout(300),
      ]);

      if (response) {
        toast.success("Successfully deleted");
        dispatch(setSelectedCompanyTypeFilter(null));
        dispatch(deleteCompanyTypeFilter(response.id));
        setIsUpdate(true);
      }
    } catch (error) {
      toast.error(
        isErrorWithMessage(error)
          ? error.data.data.message
          : "Something went wrong. Please try again"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const {
    values,
    touched,
    errors,
    setFieldValue,
    handleSubmit,
    setFieldTouched,
    setFieldError,
  } = useFormik({
    initialValues: {
      title: "",
    },
    onSubmit: selectedCompanyTypeFilter ? onUpdate : onSave,
    validationSchema: CompanyTypeFilterTitleSchema,
  });

  return (
    <div className="h-full w-full justify-between flex flex-col border-l-2 border-black">
      <div className="mx-4 ">
        <p className="font-Switzer font-bold text-black text-2xl my-4">
          {selectedCompanyTypeFilter
            ? "Update Company Type"
            : "Create Company Type"}
        </p>
        <p className="font-Switzer font-normal text-black text-base">
          {selectedCompanyTypeFilter
            ? "Update company type to the library."
            : "Add a new company type to the library."}
        </p>
        <TextInput
          label="Company Type"
          name="Company Type"
          type="text"
          value={values.title}
          required={true}
          onChange={(text) => setFieldValue("title", text)}
          isTouched={touched.title}
          error={errors.title}
        />
        <CustomButton
          text={selectedCompanyTypeFilter ? "Update Type" : "Add Type"}
          className="medium-green-button mt-6"
          onClick={handleSubmit}
          isLoading={isLoading}
          disabled={isLoading || isDeleting}
        />
      </div>
      <div>
        <CustomButton
          text="Delete Type"
          onClick={onDelete}
          className="medium-white-button mb-4 ml-4"
          loaderColor="black"
          isLoading={isDeleting}
          disabled={isLoading || isDeleting}
        />
      </div>
    </div>
  );
}
